import { Stack, Text } from "@mantine/core";
import { DataResult, EntityClientAction, FakeProgressBar, useExecuteProc } from "@mcurros2/microm";
import saveAs from "file-saver";
import JSZip from "jszip";
import { useEffect, useState } from "react";
import { API_URL, APP_ID } from "../../config";
import { Reportes } from "./Reportes";

function sanitizeSegment(s: string) {
    return (s || "")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .substring(0, 100);
}

function onlyDateYYYYMMDD(value: string) {
    const tryDate = new Date(value);
    if (!isNaN(tryDate.getTime())) {
        const y = tryDate.getFullYear();
        const m = String(tryDate.getMonth() + 1).padStart(2, "0");
        const d = String(tryDate.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    const cut = value.split(/[ T]/)[0]?.replace(/[/\\.]/g, "-");
    if (/^\d{4}-\d{2}-\d{2}$/.test(cut)) return cut;
    return "0000-00-00";
}

async function fetchWithTimeout(url: string, ms = 20000): Promise<Response> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    try {
        return await fetch(url, { signal: ctrl.signal, credentials: "omit", cache: "no-store" });
    } finally {
        clearTimeout(t);
    }
}

function avoidCollision(pathMap: Set<string>, basePath: string) {
    if (!pathMap.has(basePath)) {
        pathMap.add(basePath);
        return basePath;
    }
    const dot = basePath.lastIndexOf(".");
    const name = dot > 0 ? basePath.slice(0, dot) : basePath;
    const ext = dot > 0 ? basePath.slice(dot) : "";
    let i = 1;
    let candidate = `${name}(${i})${ext}`;
    while (pathMap.has(candidate)) {
        i++;
        candidate = `${name}(${i})${ext}`;
    }
    pathMap.add(candidate);
    return candidate;
}

async function buildZip(dr: DataResult): Promise<Blob> {
    const headers: string[] = (dr as any)?.Header ?? [];
    const rows: any[][] = (dr as any)?.records ?? [];

    const idxImagen = headers.indexOf("ValorImagen");
    const idxCliente = headers.indexOf("Cliente");
    const idxCadena = headers.indexOf("Cadena");
    const idxTienda = headers.indexOf("Tienda");
    const idxRealizado = headers.indexOf("FechaRealizado");

    if (rows.length === 0 || idxImagen < 0) {
        return new Blob([], { type: "application/zip" });
    }

    const zip = new JSZip();
    const CONCURRENCIA = 5; // habría que probar con mas datos
    let cursor = 0;

    const pathSet = new Set<string>();

    async function worker() {
        while (cursor < rows.length) {
            const i = cursor++;
            const row = rows[i];

            const imagenUrl = String(row[idxImagen] ?? "").trim();
            if (!imagenUrl) continue;

            try {

                const url_img = `${API_URL}/${APP_ID}/serve/${imagenUrl}`;

                const res = await fetchWithTimeout(url_img, 20000);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const blob = await res.blob();

                const valorImagen = String(row[idxImagen] ?? "").trim();
                const fileName = sanitizeSegment(valorImagen);

                const cliente = sanitizeSegment(idxCliente >= 0 ? String(row[idxCliente] ?? "") : "");
                const cadena = sanitizeSegment(idxCadena >= 0 ? String(row[idxCadena] ?? "") : "");
                const tienda = sanitizeSegment(idxTienda >= 0 ? String(row[idxTienda] ?? "") : "");
                const realizado = onlyDateYYYYMMDD(idxRealizado >= 0 ? String(row[idxRealizado] ?? "") : "");

                const carpetaCliente = cliente || "Cliente";
                const carpetaFecha = realizado || "Fecha";
                const carpetaCadena = cadena || "Cadena";
                const carpetaTienda = tienda || "Tienda";

                const basePath = `${carpetaCliente}/${carpetaFecha}/${carpetaCadena}/${carpetaTienda}/${fileName}`;
                const finalPath = avoidCollision(pathSet, basePath);

                zip.file(finalPath, blob, { binary: true });
            } catch (err) {
                console.warn("No se pudo descargar:", imagenUrl, err);
            }
        }
    }

    await Promise.all(Array.from({ length: Math.min(CONCURRENCIA, rows.length) }, worker));

    return zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
    });
}

export const ExportarFotosModal = ({ reportes, onClose }: { reportes: Reportes; onClose: () => void }) => {
    const getReportes = useExecuteProc(reportes, reportes.def.procs.repo_qryReportesFotos);
    const [datos0, setDatos0] = useState<DataResult>();

    useEffect(() => {
        (async () => {
            await getReportes.execute({
                c_cliente_id: reportes.def.columns.c_cliente_id.value,
                dt_fecha_desde: reportes.parentKeys.dt_fecha_desde,
                dt_fecha_hasta: reportes.parentKeys.dt_fecha_hasta,
                c_autorizacion_directa_status_id: reportes.parentKeys.c_autorizacion_directa_status_id,
                dt_fecha_realizado_desde: reportes.parentKeys.dt_fecha_realizado_desde,
                dt_fecha_realizado_hasta: reportes.parentKeys.dt_fecha_realizado_hasta,
            });
        })();
    }, [
        reportes.def.columns.c_cliente_id.value,
        reportes.parentKeys.dt_fecha_desde,
        reportes.parentKeys.dt_fecha_hasta,
        reportes.parentKeys.c_autorizacion_directa_status_id,
        reportes.parentKeys.dt_fecha_realizado_desde,
        reportes.parentKeys.dt_fecha_realizado_hasta
    ]);

    useEffect(() => {
        if (!getReportes.status.loading && getReportes.status.data) {
            setDatos0(getReportes.status.data[0]);
        }
    }, [getReportes.status]);

    useEffect(() => {
        (async () => {
            if (!datos0) return;
            try {
                const zipBlob = await buildZip(datos0);
                saveAs(zipBlob, "fotos.zip");
            } finally {
                onClose();
            }
        })();
    }, [datos0]);

    return (
        <Stack>
            <Text>Generando ZIP de imágenes…</Text>
            <FakeProgressBar />
        </Stack>
    );
};

export const ACTDescargarFotos: EntityClientAction = {
    name: "ACTDescargarFotos",
    label: "Descargar Fotos",
    title: <Text fw={700}>Descargar Fotos</Text>,
    refreshOnClose: true,
    dontRequireSelection: true,
    showActionInViewMode: true,
    views: ["repo_brwBaseReportesDetallada"],
    onClick: async ({ entity, modal, element }) => {
        const reportes = entity as Reportes;
        if (!modal) {
            console.warn("modal context not found");
            return false;
        }
        await modal.open({
            modalProps: { title: <Text fw="700">Exportar Fotos</Text> },
            focusOnClosed: element,
            content: <ExportarFotosModal reportes={reportes} onClose={() => modal.close()} />,
        });
        return false;
    },
};